import {HttpClient, HttpParams} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Item } from 'app/models/item.inferface';
import {WorkflowInstance, WorkflowStepHistoryDto} from 'app/models/workflow/workflow-instance.interface';
import {Observable, of} from 'rxjs';
import { ConfigsLoaderService } from './config-loader.service';
import { addSectionToCurrentInstaceDto, WorkflowStepInstance, WorkflowStepInstanceSection, WorkflowStepInstanceSectionCompletion, workflowStepInstanceSectionLogToSave, WorkflowStepNames} from '../models/workflow/workflow-step-instance.interface';
import { GetWorkflowInstanceMock, createWorkflowInstanceMock } from 'assets/mock/workflow-data.mock';
import {TypeOfGroup} from '../enums/types-groups.enum';
import { FollowUpTaskToAssociate } from 'app/models/follow-up-actions/follow-up-actions.model';
import { retry } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';


@Injectable({
    providedIn: 'root'
})
export class WorkflowService {

    private readonly useMockData = true;
    private readonly apiEndpoint: string;

    constructor(
        private httpClient: HttpClient,
        private config: ConfigsLoaderService,
        private translate: TranslateService,
        private dialog: MatDialog,
        private satinazer: DomSanitizer,
    ) {
        this.apiEndpoint = this.config.apiUrl;
    }

    getWorkFlowAlert(): Observable<WorkflowInstance> {
        return this.httpClient.get<WorkflowInstance>(`${this.apiEndpoint}/Workflow/GetWorkflowAlertInstance`);
    }

    getWorkFlowCase(): Observable<WorkflowInstance> {
        return this.httpClient.get<WorkflowInstance>(`${this.apiEndpoint}/Workflow/GetWorkflowCasesInstance`);
    }

    getWorkFlowCurrentInstance(WorkflowInstanceId: number): Observable<WorkflowStepInstance> {
        return this.httpClient.get<WorkflowStepInstance>(`${this.apiEndpoint}/Workflow/GetWorkflowCurrentInstance/${WorkflowInstanceId}`);
    }

    getUsers(params?: {typeOfGroup?: TypeOfGroup, description?: string}): Observable<Item[]> {
        return this.httpClient.get<Item[]>(`${this.apiEndpoint}/Workflow/GetUsers`, {params});
    }

    getUserGroups(typeOfGroup: number) {
        return this.httpClient.get<Item[]>(`${this.apiEndpoint}/Workflow/GetUserGroups?typeOfGroup=${typeOfGroup}`);
    }

    saveWorkflowInstance(instance: WorkflowInstance): Observable<WorkflowInstance> {
        return this.httpClient.post<WorkflowInstance>(`${this.apiEndpoint}/Workflow/SaveWorkflowInstance`, instance);
    }

    saveStepInstance(section: WorkflowStepInstance): Observable<string[]> {
        return this.httpClient.post<string[]>(`${this.apiEndpoint}/Workflow/SaveStepInstance`, section);
    }

    completeStepInstance(section: WorkflowStepInstance)  {
        return this.httpClient.post(`${this.apiEndpoint}/Workflow/CompleteStepInstance`, section);
    }

    getWorkflowStep(workflowInstanceId: number): Observable<WorkflowStepInstance> {
        // if (this.useMockData) {
        //     return of(createWorkflowInstanceMock(GetWorkflowInstanceMock()));
        // }
        return this.httpClient.get<WorkflowStepInstance>(`${this.apiEndpoint}/Workflow/GetWorkflowCurrentInstance/${workflowInstanceId}`);
    }

    goNextStep(workflowInstanceId): Observable<WorkflowStepInstance>{
        return this.httpClient.post<WorkflowStepInstance>(`${this.apiEndpoint}/Workflow/MoveToNextStep?description`, workflowInstanceId);
    }

    jumpNextStep(workflowInstanceId: number): Observable<WorkflowStepInstance>{
        return this.httpClient.post<WorkflowStepInstance>(`${this.apiEndpoint}/Workflow/JumpNextStep`, workflowInstanceId);
    }

    finalizeWorkFlow(workflowInstanceId: number): Observable<WorkflowStepInstance>{
        return this.httpClient.post<WorkflowStepInstance>(`${this.apiEndpoint}/Workflow/FinalizeWorkflowInstance`, workflowInstanceId);
    }

    downloadSummaryPdf(workflowInstanceId: number): Observable<WorkflowStepInstance>{
        return this.httpClient.post<WorkflowStepInstance>(`${this.apiEndpoint}/Workflow/DownloadSummaryPdf`, workflowInstanceId);
    }

    getStepNames(workflowInstanceId: number) {
        return this.httpClient.get<WorkflowStepNames[]>(`${this.apiEndpoint}/Workflow/GetStepsNames?workflowInstanceId=${workflowInstanceId}`);
    }

    getHistory(workflowInstanceId: number) {
        return this.httpClient.get<WorkflowStepHistoryDto[]>(`${this.apiEndpoint}/Workflow/GetHistory?workflowInstanceId=${workflowInstanceId}`);
    }

    getWorkflowStepInstance(workflowInstanceId): Observable<WorkflowStepInstance>{
        return this.httpClient.get<WorkflowStepInstance>(`${this.apiEndpoint}/Workflow/GetWorkflowStepInstance/${workflowInstanceId}`)
    }

    saveWorkflowStepInstanceSectionLog(logsToSave: workflowStepInstanceSectionLogToSave): Observable<workflowStepInstanceSectionLogToSave>{
        return this.httpClient.post<workflowStepInstanceSectionLogToSave>(`${this.apiEndpoint}/Workflow/AddSectionLog`, logsToSave)
    }

    saveSectionCompletion(params: WorkflowStepInstanceSectionCompletion): Observable<any>{
        return this.httpClient.post<any>(`${this.apiEndpoint}/Workflow/MarkSectionCompletion`, params)
    }

    getAllFollowUpActions(workflowInstanceId: number): Observable<FollowUpTaskToAssociate[]>{
        return this.httpClient.get<FollowUpTaskToAssociate[]>(`${this.apiEndpoint}/Workflow/GetAllFollowUpTask/${workflowInstanceId}`)
        // const mock:FollowUpTaskToAssociate[]  = [
        //     {
        //         followUpTaskId: null,
        //         entityFollowUpTaskId: null,
        //         description: 'test Manual',
        //         selected: true,
        //     },
        //     {
        //         followUpTaskId: 1,
        //         entityFollowUpTaskId: 1,
        //         description: 'test proporsal 1',
        //         selected: true,
        //     },
        //     {
        //         followUpTaskId: 2,
        //         entityFollowUpTaskId: 1,
        //         description: 'test proporsal 2',
        //         selected: false,
        //     }
        // ];
        // return of(mock);
    }

    getSectionsByPrevInstanceId(workflowInstanceId: number): Observable<WorkflowStepInstanceSection[]>{
        return this.httpClient.get<WorkflowStepInstanceSection[]>(`${this.apiEndpoint}/Workflow/GetWorkflowStepInstanceSection/${workflowInstanceId}`)
    }

    addSectionToCurrentInstance(data: addSectionToCurrentInstaceDto){
        return this.httpClient.post(`${this.apiEndpoint}/Workflow/AddSectionsWorkflowStepCurrentInstance`, data)
    }

}
